import React, {Component, Suspense} from "react";
import {connect} from "react-redux";
import URLSearchParams from 'url-search-params'
import {Redirect, Route as RouterRoute, Switch} from "react-router-dom";
import {ConfigProvider, Modal} from "antd";
import {IntlProvider} from "react-intl";

import { resolveAppLocale } from "lngProvider";
import {setInitUrl} from "appRedux/actions/Auth";
import {switchLanguage,onLayoutTypeChange, onNavStyleChange, setThemeType} from "appRedux/actions/Setting";
import axios from 'util/Api';
import {
  LAYOUT_TYPE_BOXED,
  LAYOUT_TYPE_FRAMED,
  LAYOUT_TYPE_FULL,
  NAV_STYLE_ABOVE_HEADER,
  NAV_STYLE_BELOW_HEADER,
  NAV_STYLE_DARK_HORIZONTAL,
  NAV_STYLE_DEFAULT_HORIZONTAL,
  NAV_STYLE_INSIDE_HEADER_HORIZONTAL,
  THEME_TYPE_DARK
} from "../../constants/ThemeSetting";

import {getData} from "../../functions/save";
import {matchLanguage} from "components/LanguageSelector";
import {getMe, guestSignIn} from "../../appRedux/actions/Auth";
import {
  AI_CREDIT_ALERT_MESSAGE,
  AI_CREDIT_EVENT,
  AI_CREDIT_EXHAUSTED_MESSAGE,
} from "../../util/aiCreditAlerts";
import API_ENDPOINTS from "../../util/apiEndpoints";

const MainApp = React.lazy(() => import("./MainApp"));
const SignIn = React.lazy(() => import("../SignIn"));
const SignUp = React.lazy(() => import("../SignUp"));
const LandingPage = React.lazy(() => import("../LandingPage"));

const SuperAdminLogin = React.lazy(() => import("../SuperAdminLogin"));
const SuperAdminPanel = React.lazy(() => import("../SuperAdminPanel"));
const ForgotPassword = React.lazy(() => import("../ForgotPassword"));
const ResetPassword = React.lazy(() => import("../ResetPassword"));
const PublicWebLanding = React.lazy(() => import("../../routes/PublicWebLanding"));
/* Mobil tarayıcıdan girene mağaza önerisi. Kapatılınca bir daha çıkmaz. */
import UygulamaIndirmeSeridi from "../../components/UygulamaIndirmeSeridi";
const LoyaltyQrPublic = React.lazy(() => import("../../routes/LoyaltyQrPublic"));
const LegalPages = React.lazy(() => import("../../routes/LegalPages"));
const PublicAiVideoShare = React.lazy(() => import("../../routes/PublicAiVideoShare"));
const PublicChatPage = React.lazy(() => import("../../routes/PublicChatPage"));
import { AGENT_ROUTE_VALUES } from "util/agentRouteCatalog";
import { PANEL_ROUTE_VALUES } from "util/routeAccess";

/* YÜKLENME EKRANI TEMAYA UYAR. Sabit #f8fafc idi: koyu tema kullanan
   herkes sayfa geçişlerinde açık gri bir flaş görüyordu. Değişken
   tanımlanmadan önce (tema CSS'i yüklenmeden) koyuya düşüyor — uygulama
   ağırlıklı koyu, beyaz flaş daha rahatsız edici. */
const suspenseFallback = <div style={{minHeight: "100vh", background: "var(--cp-bg, #06060f)"}} />;

/* Misafir oturumu kurulurken görünen tek ekran. Ne yaptığını söylüyor:
   ziyaretçi "boş sayfa açıldı" sanmasın. */
const GuestBootScreen = () => (
  <div
    style={{
      minHeight: "100vh",
      background: "var(--cp-bg)",
      color: "#cdd8ec",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
      gap: 14,
      fontFamily: "'Inter',-apple-system,sans-serif",
    }}
  >
    <div style={{fontSize: 15, fontWeight: 700, color: "#eef2f8"}}>Misafir olarak açılıyor…</div>
    <div style={{fontSize: 13, color: "#8fa0bd", textAlign: "center", maxWidth: 320, lineHeight: 1.6}}>
      Kayıt olmadan denemen için geçici bir çalışma alanı hazırlanıyor.
    </div>
  </div>
);

/**
 * TUZAK: tek parçalı bir yol (/salon, /construction) burada REZERVE DEĞİLSE
 * "işletmenin halka açık web sitesi slug'ı" sanılır ve uygulama yönlendirmesi
 * TAMAMEN atlanır → panel açılmaz, "Sayfa Bulunamadi" görünür (canlı bulgu).
 * Bu yüzden panel rota tablosundaki (PANEL_KEY_TO_ROUTE) her rota otomatik
 * rezerve ediliyor; yeni panel eklerken burayı güncellemek gerekmiyor.
 */
const RESERVED_PUBLIC_SLUGS = new Set([
  "",
  "index",
  "landing",
  "signin",
  "signup",
  "forgot-password",
  "reset-password",
  "signin-admin",
  "super-admin",
  "panel",
  "profile",
  "terms",
  "terms-of-service",
  "privacy",
  "privacy-policy",
  "refund",
  "refund-policy",
  "data-deletion",
  "data-deletion-instructions",
  "data-deletion-request",
  "app-help",
  "affiliate",
  "home",
  ...AGENT_ROUTE_VALUES.map((value) => String(value || "").replace(/^\/+/, "").split("/")[0]),
  ...PANEL_ROUTE_VALUES.map((value) => String(value || "").replace(/^\/+/, "").split("/")[0]),
]);

const Route = ({ component: Component, render, children, ...rest }) => (
  <RouterRoute
    {...rest}
    render={render || (Component ? (props) => <Component {...props} /> : undefined)}
    children={children}
  />
);

/* Misafirin de token'ı vardır (geçici hesap). Buraya token'sız düşmek artık
   yalnız misafir oturumu AÇILAMADIĞI durumdur — o zaman giriş istenir. */
const RestrictedRoute = ({ component: Component, token, ...rest }) => (
  <Route
    {...rest}
    render={(props) =>
      token ? (
        <Component {...props} />
      ) : (
        <Redirect
          to={{
            pathname: "/signin",
            state: { from: props.location },
          }}
        />
      )
    }
  />
);

/* MİSAFİR OTURUMU HANGİ SAYFADA AÇILIR?
   Ürünün içine giren her adreste açılır. Giriş/kayıt ekranlarında, hukuki
   sayfalarda ve işletmelerin halka açık sayfalarında AÇILMAZ — oralarda
   ziyaretçinin hesaba ihtiyacı yok, boşuna geçici tenant doğmasın. */
const NO_GUEST_PATHS = new Set([
  "/signin", "/signin-admin", "/signup", "/super-admin", "/super-admin/panel",
  "/forgot-password", "/reset-password", "/index", "/landing",
  "/terms", "/terms-of-service", "/privacy", "/privacy-policy",
  "/refund", "/refund-policy",
  "/data-deletion", "/data-deletion-instructions", "/data-deletion-request",
]);
const NO_GUEST_PREFIXES = ["/c/", "/s/", "/v/", "/scan/live/", "/loyalty-card/"];

const shouldStartGuestSession = (pathname) => {
  const p = String(pathname || "/");
  if (NO_GUEST_PATHS.has(p)) return false;
  if (NO_GUEST_PREFIXES.some((prefix) => p.indexOf(prefix) === 0)) return false;
  // Tek parçalı ve rezerve olmayan yol = işletmenin halka açık sayfası
  const parts = p.replace(/^\/+/, "").split("/").filter(Boolean);
  if (parts.length === 1 && !RESERVED_PUBLIC_SLUGS.has(parts[0])) return false;
  return true;
};

class App extends Component {
  state = {
    aiCreditBanner: null,
    // "idle" | "starting" | "failed" — misafir oturumu açma durumu
    guestBoot: "idle",
  };

  /* MİSAFİR GİRİŞİ — sayfa açılır açılmaz, kullanıcı hiçbir şey yapmadan.
     Sunucu geçici ama gerçek bir hesap (tenant + ücretsiz plan) verir; ürün
     önizleme olarak değil, kendi verisiyle çalışır. Açılamazsa (kota, ağ)
     eski yola düşülür: giriş ekranı.

     NEDEN componentDidMount DEĞİL (canlı bulgu): token yokken ilk render
     <Redirect to="/signin"> çiziyor; Redirect'in kendi didMount'u EBEVEYNDEN
     ÖNCE çalışıp adresi /signin yapıyordu. Sıra App'e geldiğinde yol artık
     "misafir açılmayan" bir yol olduğu için istek HİÇ atılmıyordu — canlıda
     ölçüldü: ağ kaydında /api/guest/start yok, ziyaretçi giriş ekranında.
     Bu yüzden karar İLK RENDER'DAN ÖNCE veriliyor. */
  bootstrapGuestSession = (pathname) => {
    if (this.props.token) return;
    // "failed" iken tekrar denemiyoruz: kota dolduysa her gezinmede sunucuya
    // yüklenmek yerine ziyaretçiyi giriş ekranına bırakmak doğrusu.
    if (this.state.guestBoot === "starting" || this.state.guestBoot === "failed") return;
    const path = pathname || (this.props.location && this.props.location.pathname);
    if (!shouldStartGuestSession(path)) return;
    this.setState({ guestBoot: "starting" });
    this.props
      .guestSignIn()
      .then((user) => {
        this.setState({ guestBoot: user ? "idle" : "failed" });
      })
      // İstek reddedilirse (ağ/CORS) state "starting"te kalıp ziyaretçiyi
      // bekleme ekranında asıyordu; "failed" ile giriş ekranına düşsün.
      .catch(() => this.setState({ guestBoot: "failed" }));
  };

  aiCreditModal = null;
  aiCreditNoticeAt = 0;
  aiCreditSummaryTimer = null;

  getAiCreditBannerFromSummary = (summaryLike) => {
    const summary = summaryLike || {};
    const usageStatus = (summary.usage_status && summary.usage_status.ai_credits) ||
      (summary.entitlements && summary.entitlements.usage_status && summary.entitlements.usage_status.ai_credits) ||
      {};
    const warnings = []
      .concat(Array.isArray(summary.warnings) ? summary.warnings : [])
      .concat(summary.entitlements && Array.isArray(summary.entitlements.warnings) ? summary.entitlements.warnings : []);

    const matchedWarning = warnings.find((item) =>
      item && (item.key === "ai_credits_exceeded" || item.key === "ai_credits_near_limit")
    );

    const limit = Number(
      summary.max_monthly_ai_credits ||
      summary.max_monthly_messages ||
      usageStatus.limit ||
      0
    );
    const used = Number(
      summary.used_monthly_ai_credits ||
      summary.used_monthly_messages ||
      usageStatus.used ||
      0
    );
    const exceeded = Boolean(
      (matchedWarning && matchedWarning.key === "ai_credits_exceeded") ||
      usageStatus.exceeded
    );
    const nearLimit = Boolean(
      !exceeded &&
      ((matchedWarning && matchedWarning.key === "ai_credits_near_limit") || usageStatus.near_limit)
    );

    if (!exceeded && !nearLimit) {
      return null;
    }

    const usageText = limit > 0 ? ` (${used}/${limit})` : "";
    return {
      key: exceeded ? "ai_credits_exceeded" : "ai_credits_near_limit",
      severity: exceeded ? "high" : "medium",
      title: exceeded ? AI_CREDIT_EXHAUSTED_MESSAGE : "AI kullanim krediniz azaliyor",
      message: exceeded
        ? `AI kullanim krediniz${usageText} tukenmis durumda. Yeni AI islemleri durduruldu.`
        : `AI kullanim kredinizin cogu${usageText} kullanildi. Yakinda yeni AI islemleri durabilir.`,
      source: "summary",
    };
  };

  syncAiCreditBannerFromSummary = (summaryLike) => {
    const nextBanner = this.getAiCreditBannerFromSummary(summaryLike);
    this.setState((prevState) => {
      const current = prevState.aiCreditBanner;
      if (!nextBanner) {
        if (!current || current.source !== "summary") return null;
        return { aiCreditBanner: null };
      }

      if (
        current &&
        current.source === "summary" &&
        current.key === nextBanner.key &&
        current.message === nextBanner.message
      ) {
        return null;
      }

      return { aiCreditBanner: nextBanner };
    });

    /* SESLİ ARAMA KREDİ UYARISI KALDIRILDI. 200 kredilik sabit eşikle
       çalışıyordu; ücretsiz plan 50 krediye inince herkes daha ilk saniyede
       kırmızı "krediniz yetmiyor" şeridi görüyordu. Kredi gerçekten bittiğinde
       zaten AI kredi şeridi çıkıyor — ikinci bir korkutmaya gerek yok. */
  };

  fetchAiCreditSummary = async () => {
    if (!this.props.token) return;
    try {
      const response = await axios.post(API_ENDPOINTS.billing.summary, {});
      const payload = response && response.data ? response.data.data : null;
      this.syncAiCreditBannerFromSummary(payload || {});
    } catch (_error) {
      // Sessiz kal: aktif uyarıyı bozmayalım.
    }
  };

  componentDidMount() {
    if (typeof window !== "undefined") {
      window.addEventListener(AI_CREDIT_EVENT, this.handleAiCreditExhausted);
      /* Arapça için rtl (v2) — açılışta bir kez; dil değişince language.js uygular. */
      try { require("util/language").applyDocumentDirection(); } catch (_e) { /* yön uygulanamazsa ltr kalır */ }
    }
    if (this.props.token) {
      this.fetchAiCreditSummary();
      this.aiCreditSummaryTimer = setInterval(this.fetchAiCreditSummary, 45000);
    }
  }

  componentWillUnmount() {
    if (typeof window !== "undefined") {
      window.removeEventListener(AI_CREDIT_EVENT, this.handleAiCreditExhausted);
    }
    if (this.aiCreditModal && typeof this.aiCreditModal.destroy === "function") {
      this.aiCreditModal.destroy();
      this.aiCreditModal = null;
    }
    if (this.aiCreditSummaryTimer) {
      clearInterval(this.aiCreditSummaryTimer);
      this.aiCreditSummaryTimer = null;
    }
  }

  handleAiCreditExhausted = (event) => {
    const detail = event && event.detail ? event.detail : {};
    const now = Date.now();
    if (now - this.aiCreditNoticeAt < 3000) {
      return;
    }
    this.aiCreditNoticeAt = now;

    if (this.aiCreditModal && typeof this.aiCreditModal.destroy === "function") {
      this.aiCreditModal.destroy();
      this.aiCreditModal = null;
    }

    const description = detail.message || AI_CREDIT_ALERT_MESSAGE;
    this.setState({
      aiCreditBanner: {
        title: detail.title || AI_CREDIT_EXHAUSTED_MESSAGE,
        message: description,
      },
    });
    this.aiCreditModal = Modal.confirm({
      title: AI_CREDIT_EXHAUSTED_MESSAGE,
      content: (
        <div>
          <div>{description}</div>
          <div style={{ marginTop: 10, color: "var(--cp-faint)", lineHeight: 1.6 }}>
            Sohbet, AI Email, Flow, AI Cagri Merkezi ve diger yeni AI islemleri kredi eklenene kadar durur.
          </div>
        </div>
      ),
      okText: "Plan Sayfasini Ac",
      cancelText: "Kapat",
      onOk: () => {
        this.aiCreditModal = null;
        if (this.props.history && typeof this.props.history.push === "function") {
          this.props.history.push("/plan");
        }
      },
      onCancel: () => {
        this.aiCreditModal = null;
      },
    });
  };

  componentDidUpdate(prevProps) {
    /* Giriş ekranından ürünün içine geçildiyse (ör. "önce bir bakayım")
       misafir oturumu orada açılır. Çıkışta AÇILMAZ: çıkan kullanıcı giriş
       ekranına düşer, arkasından sessizce geçici hesap doğmaz. */
    if (
      !this.props.token &&
      this.props.location &&
      prevProps.location &&
      this.props.location.pathname !== prevProps.location.pathname
    ) {
      this.bootstrapGuestSession();
    }

    if (this.props.token && this.props.token !== prevProps.token) {
      this.fetchAiCreditSummary();
      if (this.aiCreditSummaryTimer) clearInterval(this.aiCreditSummaryTimer);
      this.aiCreditSummaryTimer = setInterval(this.fetchAiCreditSummary, 45000);
    }

    if (!this.props.token && prevProps.token && this.aiCreditSummaryTimer) {
      clearInterval(this.aiCreditSummaryTimer);
      this.aiCreditSummaryTimer = null;
      if (this.state.aiCreditBanner && this.state.aiCreditBanner.source === "summary") {
        this.setState({ aiCreditBanner: null });
      }
    }

    if (
      this.props.token &&
      this.props.location &&
      prevProps.location &&
      this.props.location.pathname !== prevProps.location.pathname
    ) {
      this.fetchAiCreditSummary();
    }
  }

  closeAiCreditBanner = () => {
    this.setState({ aiCreditBanner: null });
  };

  openPlanPage = () => {
    if (this.props.history && typeof this.props.history.push === "function") {
      this.props.history.push("/plan");
    }
  };

  setLayoutType = (layoutType) => {
    if (layoutType === LAYOUT_TYPE_FULL) {
      document.body.classList.remove('boxed-layout');
      document.body.classList.remove('framed-layout');
      document.body.classList.add('full-layout');
    } else if (layoutType === LAYOUT_TYPE_BOXED) {
      document.body.classList.remove('full-layout');
      document.body.classList.remove('framed-layout');
      document.body.classList.add('boxed-layout');
    } else if (layoutType === LAYOUT_TYPE_FRAMED) {
      document.body.classList.remove('boxed-layout');
      document.body.classList.remove('full-layout');
      document.body.classList.add('framed-layout');
    }
  };

  setNavStyle = (navStyle) => {
    if (navStyle === NAV_STYLE_DEFAULT_HORIZONTAL ||
        navStyle === NAV_STYLE_DARK_HORIZONTAL ||
        navStyle === NAV_STYLE_INSIDE_HEADER_HORIZONTAL ||
        navStyle === NAV_STYLE_ABOVE_HEADER ||
        navStyle === NAV_STYLE_BELOW_HEADER) {
      document.body.classList.add('full-scroll');
      document.body.classList.add('horizontal-layout');
    } else {
      document.body.classList.remove('full-scroll');
      document.body.classList.remove('horizontal-layout');
    }
  };

  UNSAFE_componentWillMount() {

    /* MİSAFİR OTURUMU — İLK RENDER'DAN ÖNCE. Buradaki yol henüz hiçbir
       Redirect'in dokunmadığı GERÇEK giriş yoludur; didMount'ta bakmak geç
       kalıyor (yukarıdaki nota bak). Durum "starting" olduğu için ilk render
       giriş ekranına atmak yerine bekleme ekranını çizer. */
    this.bootstrapGuestSession(
      this.props.history && this.props.history.location
        ? this.props.history.location.pathname
        : (this.props.location && this.props.location.pathname)
    );

    // Cookie eski/gecersiz bir dil tasiyabilir; sadece tanidigimiz dilleri geri yukle.
    const storedLanguage = matchLanguage(getData("language"));
    if (storedLanguage) {
      this.props.switchLanguage(storedLanguage)
    }

    if (this.props.initURL === '') {
      const pathname = this.props.history.location.pathname;
      const loginPaths = ["/signin", "/signin-admin", "/super-admin"];
      // Don't set initURL for public landing pages (single-segment non-reserved slugs)
      const slugParts = String(pathname || "").replace(/^\/+/, "").split("/").filter(Boolean);
      const isPublicSlug = slugParts.length === 1 && !RESERVED_PUBLIC_SLUGS.has(slugParts[0]);
      if (!loginPaths.includes(pathname) && !isPublicSlug) {
        this.props.setInitUrl(pathname);
      }
    }
    const params = new URLSearchParams(this.props.location.search);
    if (params.has("theme")) {
      this.props.setThemeType(params.get('theme'));
    }
    if (params.has("nav-style")) {
      this.props.onNavStyleChange(params.get('nav-style'));
    }
    if (params.has("layout-type")) {
      this.props.onLayoutTypeChange(params.get('layout-type'));
    }
    if (params.has("layout-type")) {
      this.props.onLayoutTypeChange(params.get('layout-type'));
    }


    /* let _this=this;
     _this.props.getMe();
     setInterval(function () {
       _this.props.getMe()
     },5000)*/
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.token && nextProps.token !== this.props.token) {
      axios.defaults.headers.common['x-access-token'] = "Bearer " + nextProps.token;
      if (!nextProps.authUser) {
        this.props.getMe();
      }
    }
  }

  render() {
    const {match, location, themeType, layoutType, navStyle, locale, initURL, token, colors, authUser} = this.props;
    const { aiCreditBanner } = this.state;

    let currentLocale=resolveAppLocale(locale?.locale).messages;

    // <html lang> dogru olsun: font secimi, hecelme, ekran okuyucu ve SEO buna bakar.
    // Sayfa duzeni LTR kaliyor — Arapca metin blok icinde zaten dogru yonde akiyor,
    // tam RTL cevirme antd v3 + yuzlerce sabit marginLeft yuzunden ayri bir is.
    if (typeof document !== "undefined" && locale?.locale) {
      document.documentElement.setAttribute("lang", locale.locale);
    }

    if (themeType === THEME_TYPE_DARK) {
      document.body.classList.add('dark-theme');
    }

    // ── Legal pages: public, no auth, crawler-friendly ──
    if ([
      "/terms", "/terms-of-service",
      "/privacy", "/privacy-policy",
      "/refund", "/refund-policy",
      "/data-deletion", "/data-deletion-instructions", "/data-deletion-request",
    ].includes(location.pathname)) {
      return (
        <Suspense fallback={suspenseFallback}>
          <LegalPages />
        </Suspense>
      );
    }

    // ── Public paylaşılabilir AI chat sayfası: no auth (chatpilots.ai/c/<slug>) ──
    if (location.pathname.startsWith("/c/")) {
      const slug = location.pathname.replace("/c/", "").replace(/\/$/, "");
      return (
        <Suspense fallback={suspenseFallback}>
          <PublicChatPage match={{ params: { slug }, url: location.pathname, path: "/c/:slug" }} />
        </Suspense>
      );
    }

    // ── Public Loyalty QR pages: no auth ──
    if (location.pathname.startsWith("/s/")) {
      const scanToken = location.pathname.replace("/s/", "").replace(/\/$/, "");
      return (
        <Suspense fallback={suspenseFallback}>
          <LoyaltyQrPublic match={{ params: { scanToken }, url: location.pathname, path: "/s/:scanToken" }} />
        </Suspense>
      );
    }
    if (location.pathname.startsWith("/scan/live/")) {
      const scanToken = location.pathname.replace("/scan/live/", "");
      return (
        <Suspense fallback={suspenseFallback}>
          <LoyaltyQrPublic match={{ params: { scanToken }, url: location.pathname, path: "/scan/live/:scanToken" }} />
        </Suspense>
      );
    }
    if (location.pathname.startsWith("/loyalty-card/")) {
      const tenantSlug = location.pathname.replace("/loyalty-card/", "").replace(/\/$/, "");
      return (
        <Suspense fallback={suspenseFallback}>
          <LoyaltyQrPublic match={{ params: { tenantSlug }, url: location.pathname, path: "/loyalty-card/:tenantSlug" }} />
        </Suspense>
      );
    }

    // ── Public landing page: bypass ALL auth flow ──
    const pathParts = String(location.pathname || "").replace(/^\/+/, "").split("/").filter(Boolean);
    const publicSlugCandidate = pathParts.length === 1 ? pathParts[0] : "";
    const canRenderPublicLanding = publicSlugCandidate && !RESERVED_PUBLIC_SLUGS.has(publicSlugCandidate);

    if (location.pathname === '/index' || location.pathname === '/landing') {
      /* Şerit KENDİ tanıtım sayfamızda da çıkar — ziyaretçi henüz üye bile
         değilken uygulamayı görmesi en çok burada işe yarar. Kiracının
         KENDİ genel sayfasında (aşağıdaki PublicWebLanding) çıkmaz: orası
         onun markası, ziyaretçisi de bizim değil onun müşterisi. */
      return (
        <Suspense fallback={suspenseFallback}>
          <React.Fragment>
            <LandingPage />
            <UygulamaIndirmeSeridi />
          </React.Fragment>
        </Suspense>
      );
    }

    if (canRenderPublicLanding) {
      return (
        <Suspense fallback={suspenseFallback}>
          <PublicWebLanding match={{ params: { slug: publicSlugCandidate }, url: '/' + publicSlugCandidate, path: '/:slug' }} location={location} />
        </Suspense>
      );
    }

    /* MİSAFİR OTURUMU AÇILIYOR — ziyaretçi hiçbir şey yapmadan geçici hesabı
       kuruluyor. Bir saniyelik iş; giriş ekranı ya da yarım kabuk göstermek
       yerine sade bir bekleme ekranı çiziyoruz.

       "idle + açılacak" hâlini de burada yakalıyoruz: bootstrapGuestSession
       componentDidMount/DidUpdate'te, yani ilk render'DAN SONRA çalışıyor.
       Sadece "starting"e baksaydık o ilk render RestrictedRoute'a düşer ve
       ziyaretçi daha misafir oturumu başlamadan /signin'e atılırdı — /home
       gibi korumalı adreslere dışarıdan gelmek bu yüzden imkânsızdı. */
    const guestBootPending =
      this.state.guestBoot === "starting" ||
      (this.state.guestBoot === "idle" && shouldStartGuestSession(location.pathname));
    if (!token && guestBootPending) {
      return <GuestBootScreen />;
    }

    if (location.pathname === '/') {
      if (token) {
        if (initURL === '' || initURL === '/' || initURL === '/signin') {
          return <Redirect to={'/panel'} />;
        }
        return <Redirect to={initURL} />;
      }
      /* Misafir oturumu açılamadı (kota/ağ): eski yola düş, giriş iste.
         Eski pazarlama sayfası /landing (ve /index) adresinde duruyor. */
      return <Redirect to={'/signin'} />;
    }

    this.setLayoutType(layoutType);
    this.setNavStyle(navStyle);


    const currentAppLocale = resolveAppLocale(locale?.locale);
    return (
        <ConfigProvider locale={currentAppLocale.antd}>
          <IntlProvider
              locale={currentAppLocale.locale}
              messages={currentAppLocale.messages}>
            <Suspense fallback={suspenseFallback}>
              <React.Fragment>
                <div
                  style={{
                    minHeight: token ? "100vh" : undefined,
                    width: "100%",
                    background: token ? "var(--cp-bg)" : undefined,
                  }}
                >
                  <Switch>
                    {/* Public AI video share viewer — auth gerektirmez. Path: /v/:token.
                        Backend share_url'i bu route'u hedefler (chatpilots.ai/v/<token>). */}
                    <Route exact path='/v/:token' component={PublicAiVideoShare} />
                    <Route
                      exact
                      path='/signin'
                      render={(props) => <SignIn {...props} loginVariant="customer" />}
                    />
                    <Route exact path='/forgot-password' component={ForgotPassword} />
                    <Route exact path='/reset-password' component={ResetPassword} />
                    <Route
                      exact
                      path='/signin-admin'
                      render={(props) => <SignIn {...props} loginVariant="admin" />}
                    />
                    <Route exact path='/super-admin' component={SuperAdminLogin} />
                    <RestrictedRoute exact path='/super-admin/panel' token={token} component={SuperAdminPanel} />
                    <Route exact path='/signup' component={SignUp}/>
                    <RestrictedRoute path={`${match.url}`} token={token} component={MainApp}/>
                  </Switch>
                </div>
                {/* İNDİRME ŞERİDİ. Kredi uyarısı varken çıkmaz: ikisi de
                    alta sabitleniyor ve üst üste binerdi; ayrıca kredi
                    bitmesi acil, mağaza önerisi değil. */}
                <UygulamaIndirmeSeridi
                  gizle={Boolean(
                    (token && aiCreditBanner) ||
                    /* Super admin panelinde magaza banner'i cikmasin — yonetici
                       ekrani, musteri promosyonu degil; icerigi kapatiyordu. */
                    String((this.props.location && this.props.location.pathname) || "").indexOf("/super-admin") === 0
                  )}
                />
                {token && aiCreditBanner ? (
                  <div
                    style={{
                      position: "fixed",
                      bottom: 20,
                      left: "50%",
                      transform: "translateX(-50%)",
                      maxWidth: 580,
                      width: "calc(100% - 32px)",
                      padding: "12px 18px",
                      background: aiCreditBanner.severity === "high"
                        ? "rgba(153,27,27,0.95)"
                        : "rgba(124,45,18,0.95)",
                      backdropFilter: "blur(12px)",
                      color: "#fff",
                      boxShadow: aiCreditBanner.severity === "high"
                        ? "0 8px 32px rgba(220,38,38,0.3)"
                        : "0 8px 32px rgba(245,158,11,0.25)",
                      zIndex: 2147482000,
                      borderRadius: 14,
                      border: aiCreditBanner.severity === "high"
                        ? "1px solid rgba(239,68,68,0.3)"
                        : "1px solid rgba(245,158,11,0.3)",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                        <span style={{ fontSize: 18, flexShrink: 0 }}>{aiCreditBanner.severity === "high" ? "⚠️" : "💡"}</span>
                        <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.4 }}>
                          {aiCreditBanner.severity === "high"
                            ? "AI krediniz tükendi. Yeni AI işlemleri durdu."
                            : "AI krediniz azalıyor. Yakında AI işlemleri durabilir."}
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                        <button
                          type="button"
                          onClick={this.openPlanPage}
                          style={{
                            border: "none",
                            borderRadius: 8,
                            padding: "7px 14px",
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: "pointer",
                            background: "#fff",
                            color: aiCreditBanner.severity === "high" ? "#991b1b" : "#92400e",
                            transition: "transform 0.15s",
                          }}
                        >
                          Kredi Al
                        </button>
                        <button
                          type="button"
                          onClick={this.closeAiCreditBanner}
                          style={{
                            border: "none",
                            borderRadius: 8,
                            padding: "7px 10px",
                            fontSize: 14,
                            cursor: "pointer",
                            background: "rgba(255,255,255,0.1)",
                            color: "rgba(255,255,255,0.7)",
                            lineHeight: 1,
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}
              </React.Fragment>
            </Suspense>
          </IntlProvider>
        </ConfigProvider>
    )
  }
}

const mapStateToProps = ({settings, auth}) => {
  const {locale, navStyle, themeType, layoutType, colors} = settings;
  const {token, initURL, authUser} = auth;
  return {locale, token, navStyle, themeType, layoutType, initURL, colors, authUser}
};
export default connect(mapStateToProps, {getMe,guestSignIn,switchLanguage,setInitUrl, setThemeType, onNavStyleChange, onLayoutTypeChange})(App);
